import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { productService } from '../lib/firebaseService';
import { FileDown, FileSpreadsheet, Calendar, Archive } from 'lucide-react';
import * as XLSX from 'xlsx';
import { t } from '../utils/translation';
import '../styles/components-extended.css';

export default function Reports() {
    const { data: products } = useQuery({ queryKey: ['products'], queryFn: productService.getProducts });
    const { data: events } = useQuery({ queryKey: ['events'], queryFn: productService.getEvents });
    const { data: movements } = useQuery({ queryKey: ['movements'], queryFn: productService.getMovements });

    const exportInventory = () => {
        if (!products) return;

        // Format data for export
        const data = products.map(p => ({
            'Category': p.category,
            'Brand': p.brand,
            'Product Name': p.name,
            'Unit Type': p.unitType,
            'ABV (%)': p.abv,
            'Cost Price': p.cost,
            'Selling Price': p.price,
            'Current Stock': p.stock,
            'Stock Value': p.stock * p.cost
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Inventory");
        XLSX.writeFile(wb, `Inventory_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const exportMovements = () => {
        if (!movements) return;

        const data = movements.map(m => ({
            'Date': new Date(m.date).toLocaleString(),
            'Product': m.productName,
            'Type': m.type.toUpperCase(),
            'Quantity': m.qty,
            'Reason': m.reason,
            'User': m.user
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Movements");
        XLSX.writeFile(wb, `Stock_Movements_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const exportEvents = () => {
        if (!events) return;

        const data = events.map(e => ({
            'Event Name': e.name,
            'Date': new Date(e.date).toLocaleDateString(),
            'Venue': e.venue,
            'Guest Count': e.guestCount,
            'Revenue': e.revenue,
            'Status': e.status
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Events");
        XLSX.writeFile(wb, `Events_Summary_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">{t('REPORTS.TITLE')}</h1>
                <p className="text-gray-500 text-sm mt-1">{t('REPORTS.SUBTITLE')}</p>
            </div>

            {/* Dashboard-like Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                {/* Inventory Export Card */}
                <div className="content-card flex flex-col items-start h-full hover:border-purple-200 transition-colors cursor-default group">
                    <div className="p-3 bg-green-50 text-green-600 rounded-xl mb-5 group-hover:scale-110 transition-transform">
                        <Archive size={28} />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{t('REPORTS.INVENTORY.TITLE')}</h3>
                    <p className="text-gray-500 mb-8 flex-1 leading-relaxed">
                        {t('REPORTS.INVENTORY.DESC')}
                    </p>
                    <button onClick={exportInventory} className="btn btn-secondary w-full justify-center border-gray-200 hover:border-purple-300 hover:text-purple-700">
                        <FileDown size={18} className="mr-2" /> {t('REPORTS.INVENTORY.BTN')}
                    </button>
                </div>

                {/* Movements Export Card */}
                <div className="content-card flex flex-col items-start h-full hover:border-purple-200 transition-colors cursor-default group">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-xl mb-5 group-hover:scale-110 transition-transform">
                        <FileSpreadsheet size={28} />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{t('REPORTS.MOVEMENTS.TITLE')}</h3>
                    <p className="text-gray-500 mb-8 flex-1 leading-relaxed">
                        {t('REPORTS.MOVEMENTS.DESC')}
                    </p>
                    <button onClick={exportMovements} className="btn btn-secondary w-full justify-center border-gray-200 hover:border-purple-300 hover:text-purple-700">
                        <FileDown size={18} className="mr-2" /> {t('REPORTS.MOVEMENTS.BTN')}
                    </button>
                </div>

                {/* Events Export Card */}
                <div className="content-card flex flex-col items-start h-full hover:border-purple-200 transition-colors cursor-default group">
                    <div className="p-3 bg-purple-50 text-purple-600 rounded-xl mb-5 group-hover:scale-110 transition-transform">
                        <Calendar size={28} />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{t('REPORTS.EVENTS.TITLE')}</h3>
                    <p className="text-gray-500 mb-8 flex-1 leading-relaxed">
                        {t('REPORTS.EVENTS.DESC')}
                    </p>
                    <button onClick={exportEvents} className="btn btn-secondary w-full justify-center border-gray-200 hover:border-purple-300 hover:text-purple-700">
                        <FileDown size={18} className="mr-2" /> {t('REPORTS.EVENTS.BTN')}
                    </button>
                </div>

            </div>
        </div>
    );
}
